import { ChangeEvent, useRef, useState } from 'react';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useQueryClient } from '@tanstack/react-query';

import useEditFile from '@/hooks/mutations/file/useEditFile';
import { isTriggerUploadingRequestAtom } from '@/state/file/uploadingfile.atom';

interface Props {
  fileId: number;
  handleEditModalClose: () => void;
}

const useHandleEditFile = ({ fileId, handleEditModalClose }: Props) => {
  const router = useRouter();
  const queryClient = useQueryClient();

  const fileUploadRef = useRef<HTMLInputElement>(null);

  const setIsTriggerUploadingRequest = useSetAtom(
    isTriggerUploadingRequestAtom,
  );

  const [file, setFile] = useState<File | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [isFileChange, setIsFileChange] = useState(false);

  const { mutate: editFile } = useEditFile({
    onSuccess: () => {
      setIsLoading(false);
      handleEditModalClose();
      if (isFileChange) {
        router.replace('/user/pdf');
      }
      setIsTriggerUploadingRequest(true);

      queryClient.invalidateQueries({ queryKey: ['getFileList'] });
    },
  });

  const handleFileUploadClick = () => {
    if (fileUploadRef.current) {
      fileUploadRef.current.click();
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFile(e.target.files?.[0]);
      setIsFileChange(true);
    }
  };

  const getFileFormData = ({
    fileName,
    isFileChange,
  }: {
    fileName?: string;
    isFileChange: boolean;
  }) => {
    const formData = new FormData();
    if (isFileChange && file) {
      formData.append('pdf', file);
    }

    formData.append('fileId', String(fileId));

    if (fileName) {
      formData.append('name', fileName);
    }

    formData.append('isFileEdited', String(isFileChange));

    return formData;
  };

  const handleEditButtonClick = ({ fileName }: { fileName: string }) => {
    setIsLoading(true);
    const formData = getFileFormData({ fileName, isFileChange });
    editFile(formData);
  };

  return {
    file,
    isLoading,
    fileUploadRef,
    isFileChange,
    handleFileUploadClick,
    handleFileChange,
    handleEditButtonClick,
  };
};

export default useHandleEditFile;
